import Archive from "@mui/icons-material/Archive";
import FileCopyIcon from "@mui/icons-material/FileCopyOutlined";
import Unarchive from "@mui/icons-material/Unarchive";
import type { PopoverOrigin } from "@mui/material";
import { Card, Menu, Skeleton, Tooltip, Typography } from "@mui/material";
import copy from "clipboard-copy";
import type { MouseEventHandler } from "react";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  CandidatesIcon,
  GemdTableIcon,
  MenuIcon,
  PredictorIcon,
  SearchSpaceIcon,
  TerminalMaterialIcon,
} from "@citrine/atoms/icons";
import { KeyValuePair } from "@citrine/atoms/key-value";
import type { IProject, ProjectMetadata } from "@citrine/resources/project";
import { ProjectClient } from "@citrine/resources/project";
import { formatDatetime } from "@citrine/utils/datetime";

import { useHasWritePermission } from "#src/hooks/use-get-active-user-permissions-in-project";
import { useUser } from "#src/hooks/use-user";

import * as sx from "./project-card.sx";

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};

const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

function ProjectMenu({
  project,
  className,
}: {
  project: IProject;
  className?: string;
}) {
  const hasWritePerm = useHasWritePermission(project.id);

  const [menuAnchorElement, setMenuAnchorElement] =
    useState<HTMLElement | null>(null);

  const menuButtonClicked = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => setMenuAnchorElement(e.currentTarget),
    []
  );

  const copyProjectClicked = useCallback(() => {
    setMenuAnchorElement(null);
    copy(project.id);
  }, [project.id]);

  const copyTeamClicked = useCallback(() => {
    setMenuAnchorElement(null);
    copy(project.team.id);
  }, [project.team.id]);

  const projectClient = ProjectClient.getInstance();
  const { mutateAsync: archiveProject } = projectClient.useArchive();
  const archiveClicked = useCallback(() => {
    setMenuAnchorElement(null);
    archiveProject(project);
  }, [archiveProject, project]);

  const { mutateAsync: restoreProject } = projectClient.useRestore();
  const restoreClicked = useCallback(() => {
    setMenuAnchorElement(null);
    restoreProject(project);
  }, [restoreProject, project]);

  const onClose = useCallback(() => setMenuAnchorElement(null), []);
  return (
    <>
      <sx.MenuButton
        data-testid="project-card-menu-button"
        data-fullstoryid="projects.card.menu-button"
        className={className}
        onClick={menuButtonClicked}
        size="small"
      >
        <MenuIcon />
      </sx.MenuButton>
      <Menu
        anchorEl={menuAnchorElement}
        keepMounted
        open={Boolean(menuAnchorElement)}
        onClose={onClose}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
      >
        <sx.MenuItem
          data-fullstoryid="projects.card.menu.copy-project-id"
          onClick={copyProjectClicked}
        >
          <FileCopyIcon />
          <FormattedMessage id="resources.project.list.copy-project" />
        </sx.MenuItem>
        <sx.MenuItem
          data-fullstoryid="projects.card.menu.copy-team-id"
          onClick={copyTeamClicked}
        >
          <FileCopyIcon />
          <FormattedMessage id="resources.project.list.copy-team" />
        </sx.MenuItem>
        {hasWritePerm ? (
          project.archived ? (
            <sx.MenuItem onClick={restoreClicked}>
              <Unarchive />
              <FormattedMessage id="Restore" />
            </sx.MenuItem>
          ) : (
            <sx.MenuItem onClick={archiveClicked}>
              <Archive />
              <FormattedMessage id="Archive" />
            </sx.MenuItem>
          )
        ) : null}
      </Menu>
    </>
  );
}

type ProjectCountType =
  | "materials"
  | "tables"
  | "models"
  | "search-spaces"
  | "candidate-executions";

function ProjectCountIcon({ type }: { type: ProjectCountType }) {
  switch (type) {
    case "materials":
      return <TerminalMaterialIcon />;
    case "tables":
      return <GemdTableIcon />;
    case "candidate-executions":
      return <CandidatesIcon />;
    case "models":
      return <PredictorIcon />;
    case "search-spaces":
      return <SearchSpaceIcon />;
  }
}

function ProjectCount({
  type,
  value,
}: {
  type: ProjectCountType;
  value?: number;
}) {
  return (
    <Tooltip
      title={
        <FormattedMessage
          id={`resources.project.card.${type}-count-tooltip`}
          values={{ value }}
        />
      }
    >
      <sx.NumberContainer data-testid={`project-count-${type}`}>
        <ProjectCountIcon type={type} />
        {value !== undefined ? (
          <sx.Number>{value?.toLocaleString()}</sx.Number>
        ) : (
          <Skeleton>
            <sx.Number>-</sx.Number>
          </Skeleton>
        )}
      </sx.NumberContainer>
    </Tooltip>
  );
}

export function ProjectCard({
  project,
  metadata,
}: {
  project: IProject;
  metadata?: ProjectMetadata;
}) {
  // created_by is not always present in older projects since we just started recording it in April 2025
  // Using useUser hook instead of User component so we can conditionally render the created_by detail
  const user = useUser(project.created_by);
  return (
    <Card
      data-testid="project-card"
      sx={project.archived ? { opacity: 0.4 } : undefined}
    >
      <sx.Header>
        <sx.Name title={project.name}>
          <sx.Link to={`/projects/${project.id}`}>
            <sx.NameText variant="body1">{project.name}</sx.NameText>
          </sx.Link>
        </sx.Name>
        <sx.TeamContainer tooltip={false}>
          <sx.Team team={project.team} />
        </sx.TeamContainer>
        <ProjectMenu project={project} />
      </sx.Header>
      <sx.CardData>
        {metadata?.counts ? (
          <sx.CardDataRow data-testid="project-counts">
            <ProjectCount type="tables" value={metadata?.counts.tables_count} />
            <ProjectCount type="models" value={metadata?.counts.models} />
            <ProjectCount
              type="search-spaces"
              value={metadata?.counts.search_spaces}
            />
            <ProjectCount
              type="candidate-executions"
              value={metadata?.counts.candidate_executions}
            />
          </sx.CardDataRow>
        ) : (
          <sx.CardDataRow as={Skeleton}>
            <ProjectCount type="materials" value={1000} />
            <ProjectCount type="materials" value={100} />
          </sx.CardDataRow>
        )}
        <sx.Description variant="body2">{project.description}</sx.Description>

        <sx.CardDataRow>
          <KeyValuePair
            label={
              <Typography variant="h5" component="span">
                <FormattedMessage id="Created On" />
              </Typography>
            }
            content={
              <Typography variant="caption">
                {formatDatetime(project.created_at, "D MMMM YYYY")}
              </Typography>
            }
          />
          {user ? (
            <KeyValuePair
              label={
                <Typography variant="h5" component="span">
                  <FormattedMessage id="Created By" />
                </Typography>
              }
              content={
                <Typography variant="caption">{user.screen_name}</Typography>
              }
            />
          ) : null}
        </sx.CardDataRow>
      </sx.CardData>
    </Card>
  );
}
